/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
// Backstage Repo File Location - plugins\org\src\components\Cards\Group\MembersList\MembersListCard.test.tsx
import {
  DEFAULT_NAMESPACE,
  GroupEntity,
  UserEntity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  catalogApiRef,
  EntityRefLink,
  useEntity,
} from '@backstage/plugin-catalog-react';
import {
  Box,
  createStyles,
  Grid,
  IconButton,
  makeStyles,
  Switch,
  Theme,
  Typography,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import { ChangeEvent, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';

import {
  Avatar,
  InfoCard,
  Progress,
  ResponseErrorPanel,
  Link,
  OverflowTooltip,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  getAllDesendantMembersForGroupEntity,
  removeDuplicateEntitiesFrom,
} from './helpers/helpers';
import MsTeamsIcon from '../../icons/MsTeamsIcon';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[2],
      borderRadius: '4px',
      overflow: 'visible',
      position: 'relative',
      margin: theme.spacing(4, 1, 1),
      flex: '1',
      minWidth: '0px',
    },
  }),
);

const MemberComponent = (props: { member: UserEntity }) => {
  const classes = useStyles();
  const {
    metadata: { name: metaName, description },
    spec: { profile },
  } = props.member;
  const displayName = profile?.displayName ?? metaName;

  const onClickChat = (email: any) => {
    window.open(
      `https://teams.microsoft.com/l/chat/0/0?users=${email}`,
      '_blank',
      'noreferrer',
    );
  };

  return (
    <Box className={classes.card}>
      <Box
        display="flex"
        flexDirection="column"
        m={3}
        alignItems="center"
        justifyContent="center"
      >
        <Avatar
          displayName={displayName}
          picture={profile?.picture}
          customStyles={{
            position: 'absolute',
            top: '-2rem',
          }}
        />
        <Box
          pt={2}
          sx={{
            width: '100%',
          }}
          textAlign="center"
        >
          <Typography variant="h6">
            <EntityRefLink
              data-testid="user-link"
              entityRef={props.member}
              title={displayName}
            />
          </Typography>
          {profile?.email && (
            <Link to={`mailto:${profile.email}`}>
              <OverflowTooltip text={profile.email} />
            </Link>
          )}
          {description && (
            <Typography variant="subtitle2">{description}</Typography>
          )}
          <IconButton
            aria-label="MS Teams"
            title="Chat in MS Teams"
            onClick={() => {
              onClickChat(profile?.email);
            }}
          >
            <MsTeamsIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

const useListStyles = makeStyles(theme => ({
  root: {
    height: '100%',
  },
  cardContent: {
    overflow: 'auto',
  },
  memberList: {
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: `repeat(auto-fit, minmax(auto, ${theme.spacing(
      34,
    )}px))`,
  },
}));

const getAllMembers = (
  directMembers?: UserEntity[],
  descendantMembers?: UserEntity[],
  showAggregateMembers?: boolean,
) => {
  return [
    ...(directMembers ?? []),
    ...(descendantMembers && showAggregateMembers ? descendantMembers : []),
  ];
};

const renderMemberList = (
  members: UserEntity[],
  page: number,
  pageSize: number,
  memberDisplayTitle: string,
  classes: ClassNameMap<'root' | 'cardContent' | 'memberList'>,
) => {
  if (members && members.length > 0) {
    return (
      <Box className={classes.memberList}>
        {members.slice(pageSize * (page - 1), pageSize * page).map(member => (
          <MemberComponent member={member} key={member.metadata.uid} />
        ))}
      </Box>
    );
  }
  return (
    <Box p={2}>
      <Typography>
        This group has no {memberDisplayTitle.toLocaleLowerCase()}.
      </Typography>
    </Box>
  );
};

/** @public */
export const MembersListCard = (props: {
  memberDisplayTitle?: string;
  pageSize?: number;
  showAggregateMembersToggle?: boolean;
}) => {
  const {
    memberDisplayTitle = 'Members',
    pageSize = 50,
    showAggregateMembersToggle,
  } = props;
  const classes = useListStyles();

  const { entity: groupEntity } = useEntity<GroupEntity>();
  const {
    metadata: { name: groupName, namespace: grpNamespace },
    spec: { profile },
  } = groupEntity;
  const catalogApi = useApi(catalogApiRef);

  const displayName = profile?.displayName ?? groupName;

  const groupNamespace = grpNamespace || DEFAULT_NAMESPACE;

  const [page, setPage] = useState(1);
  const pageChange = (_: ChangeEvent<unknown>, pageIndex: number) => {
    setPage(pageIndex);
  };

  const [showAggregateMembers, setShowAggregateMembers] = useState(false);

  const { loading: loadingDescendantMembers, value: descendantMembers } =
    useAsync(async () => {
      if (!showAggregateMembersToggle) {
        return [] as UserEntity[];
      }

      return getAllDesendantMembersForGroupEntity(groupEntity, catalogApi);
    }, [catalogApi, groupEntity, showAggregateMembersToggle]);
  const {
    loading,
    error,
    value: directMembers,
  } = useAsync(async () => {
    const membersList = await catalogApi.getEntities({
      filter: {
        kind: 'User',
        'relations.memberof': [
          stringifyEntityRef({
            kind: 'group',
            namespace: groupNamespace.toLocaleLowerCase('en-US'),
            name: groupName.toLocaleLowerCase('en-US'),
          }),
        ],
      },
    });

    return membersList.items as UserEntity[];
  }, [catalogApi, groupEntity]);

  const members = removeDuplicateEntitiesFrom(
    getAllMembers(directMembers, descendantMembers, showAggregateMembers).sort(
      (a, b) => stringifyEntityRef(a).localeCompare(stringifyEntityRef(b)),
    ),
  ) as UserEntity[];

  if (loading) {
    return <Progress />;
  } else if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  const nbPages = Math.ceil((members?.length || 0) / pageSize);
  const paginationLabel = nbPages < 2 ? '' : `, page ${page} of ${nbPages}`;

  const pagination = (
    <Pagination
      count={nbPages}
      page={page}
      onChange={pageChange}
      showFirstButton
      showLastButton
    />
  );

  return (
    <Grid item className={classes.root}>
      <InfoCard
        title={`${memberDisplayTitle} (${
          members?.length || 0
        }${paginationLabel})`}
        subheader={`of ${displayName}`}
        {...(nbPages <= 1 ? {} : { actions: pagination })}
        className={classes.root}
        cardClassName={classes.cardContent}
      >
        {showAggregateMembersToggle && (
          <>
            Direct Members
            <Switch
              color="primary"
              checked={showAggregateMembers}
              onChange={() => {
                setShowAggregateMembers(!showAggregateMembers);
              }}
              inputProps={{ 'aria-label': 'Users Type Switch' }}
            />
            Aggregated Members
          </>
        )}
        {showAggregateMembers && loadingDescendantMembers ? (
          <Progress />
        ) : (
          renderMemberList(members, page, pageSize, memberDisplayTitle, classes)
        )}
      </InfoCard>
    </Grid>
  );
};
